/**
 * Shared helpers for the Salvation Team console: React Query keys, paths, API error copy and date formatting.
 * All wording is South African English without long dashes (member-facing rule).
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { SALVATION_ERROR_CODES } from '@actschurch/ccms-sdk';
import type { SalvationPeopleQuery } from '@actschurch/ccms-sdk';

dayjs.extend(relativeTime);

export const salvationKeys = {
  all: ['salvation'] as const,
  lookups: () => [...salvationKeys.all, 'lookups'] as const,
  settings: () => [...salvationKeys.all, 'settings'] as const,
  overview: (teamId?: number | null) => [...salvationKeys.all, 'overview', teamId ?? 'all'] as const,
  people: (query?: SalvationPeopleQuery) => [...salvationKeys.all, 'people', query ?? {}] as const,
  peopleRoot: () => [...salvationKeys.all, 'people'] as const,
  person: (id: number) => [...salvationKeys.all, 'person', id] as const,
  teams: () => [...salvationKeys.all, 'teams'] as const,
  campuses: () => [...salvationKeys.all, 'campuses'] as const,
  members: (teamId: number) => [...salvationKeys.all, 'members', teamId] as const,
  allocation: (teamId: number) => [...salvationKeys.all, 'allocation', teamId] as const,
};

export const salvationPaths = {
  overview: '/app/salvation',
  people: '/app/salvation/people',
  person: (id: number) => `/app/salvation/people/${id}`,
  teams: '/app/salvation/teams',
  team: (id: number) => `/app/salvation/teams/${id}`,
  settings: '/app/salvation/settings',
};

// ---------------------------------------------------------------- errors

/** The machine code the API puts in errors[0] (SALVATION_ERROR_CODES), if any. */
export function salvationErrorCode(err: unknown): string | undefined {
  const e = err as { errors?: unknown } | null;
  if (e && Array.isArray(e.errors) && typeof e.errors[0] === 'string') return e.errors[0] as string;
  return undefined;
}

/** HTTP status of an SDK error, if any. */
export function salvationErrorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

/** The possible duplicate a 409 duplicate_convert points at ("convertId:N" in errors[1]). */
export function duplicateConvertId(err: unknown): number | undefined {
  const e = err as { errors?: unknown } | null;
  if (!e || !Array.isArray(e.errors)) return undefined;
  const ref = (e.errors as unknown[]).find((x): x is string => typeof x === 'string' && x.startsWith('convertId:'));
  const id = ref ? Number(ref.slice('convertId:'.length)) : NaN;
  return Number.isFinite(id) ? id : undefined;
}

const ERROR_COPY: Record<string, string> = {
  [SALVATION_ERROR_CODES.NoAccess]: "You don't have access to do that.",
  [SALVATION_ERROR_CODES.NotYourCampus]: 'That campus is not one you lead.',
  [SALVATION_ERROR_CODES.ConvertNotFound]: 'That person could not be found.',
  [SALVATION_ERROR_CODES.TeamNotFound]: 'That campus team could not be found.',
  [SALVATION_ERROR_CODES.AssignmentPermanent]: 'Their Lead and Backup are permanent. Use Correct assignment, with a reason, to change them.',
  [SALVATION_ERROR_CODES.SameLeadAndBackup]: 'The Lead and the Backup must be two different people.',
  [SALVATION_ERROR_CODES.NotEnoughTeam]: 'The campus team needs at least two active members first.',
  [SALVATION_ERROR_CODES.NotInFollowUp]: 'This person is past follow-up. Add a note, or reopen them first.',
  [SALVATION_ERROR_CODES.InvalidPhone]: 'That phone number does not look right. Use a number like 082 123 4567.',
  [SALVATION_ERROR_CODES.ContactRequired]: 'Enter a phone number or an email address.',
  [SALVATION_ERROR_CODES.Stale]: 'Someone else changed this person a moment ago. Reload and try again.',
};

/** Friendly copy for an API error: the code's copy, else the server message with its field details. */
export function salvationErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const code = salvationErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  const status = salvationErrorStatus(err);
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That couldn't be found. It may have been removed.";
  const e = err as { message?: string; errors?: unknown };
  const details = Array.isArray(e.errors)
    ? (e.errors as unknown[]).filter((x): x is string => typeof x === 'string' && !/^[a-z_]+$/.test(x) && !x.startsWith('convertId:'))
    : [];
  const base = e.message || fallback;
  return details.length ? `${base}: ${details.join('; ')}` : base;
}

// ---------------------------------------------------------------- dates

/** ISO instant → "3 Nov 2026, 14:05" in the browser's time zone. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : '';
}

/** "yyyy-MM-dd" → "3 Nov 2026". */
export function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const d = dayjs(day);
  return d.isValid() ? d.format('D MMM YYYY') : day;
}

/** ISO instant → "2 hours ago" / "in 3 days". */
export function relative(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.fromNow() : '';
}

/** Today as "yyyy-MM-dd" (browser time). */
export function todayYmd(): string {
  return dayjs().format('YYYY-MM-DD');
}

/** 2359 → "23:59". */
export function hhmmToTime(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${String(Math.floor(value / 100)).padStart(2, '0')}:${String(value % 100).padStart(2, '0')}`;
}

/** "23:59" → 2359 (null when not a time). */
export function timeToHhmm(value: string | null | undefined): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h <= 23 && min <= 59 ? h * 100 + min : null;
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Saves a CSV Blob the API returned. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
